/**
 * useKnownEntities Hook
 *
 * Fetches known project entities (personas, competitors, BMC items)
 * and provides text-matching against document content.
 * Used by Smart Docs to highlight known entities in the editor.
 */

import { useQuery } from '@tanstack/react-query'
import { useSessionStore } from '@/stores/sessionStore'
import { personaWorkshopApi, competitorMapApi, businessModelApi } from '@/lib/api/client'

// ============================================================================
// TYPES
// ============================================================================

export interface KnownEntity {
  id: string
  name: string
  type: string          // e.g. 'Persona', 'Competitor', 'CustomerSegment'
  domain: 'market' | 'business' | 'solution'
  source: string        // e.g. 'persona-workshop', 'competitor-map', 'bmc'
}

export interface KnownEntityMatch {
  entity: KnownEntity
  evidence: string      // the matched text in the document
  index: number         // position in plainText
}

// ============================================================================
// BMC BLOCK → ENTITY TYPE MAPPING
// ============================================================================

const BMC_BLOCK_CONFIG: Record<string, { type: string; domain: 'market' | 'business' | 'solution' }> = {
  keyPartners: { type: 'KeyPartnership', domain: 'business' },
  keyActivities: { type: 'KeyActivity', domain: 'business' },
  keyResources: { type: 'KeyResource', domain: 'business' },
  valuePropositions: { type: 'ValueProposition', domain: 'business' },
  customerRelationships: { type: 'Channel', domain: 'business' },
  channels: { type: 'Channel', domain: 'business' },
  customerSegments: { type: 'CustomerSegment', domain: 'market' },
  costStructure: { type: 'CostStructure', domain: 'business' },
  revenueStreams: { type: 'RevenueStream', domain: 'business' },
}

// ============================================================================
// HOOK
// ============================================================================

export function useKnownEntities() {
  const { accountId, projectId, isReady } = useSessionStore()

  return useQuery({
    queryKey: ['knownEntities', accountId, projectId],
    queryFn: async (): Promise<KnownEntity[]> => {
      if (!accountId || !projectId) return []

      const entities: KnownEntity[] = []

      // Fetch all sources in parallel
      const [personaSessions, competitors, bmcCanvas] = await Promise.allSettled([
        personaWorkshopApi.listSessions(accountId, projectId),
        competitorMapApi.listCompetitors(accountId, projectId),
        businessModelApi.getCanvas(accountId, projectId),
      ])

      // Extract personas
      if (personaSessions.status === 'fulfilled' && Array.isArray(personaSessions.value)) {
        for (const session of personaSessions.value) {
          const data = session.personaData
          if (data?.name) {
            entities.push({
              id: `persona-${session.id}`,
              name: data.name,
              type: 'Persona',
              domain: 'market',
              source: 'persona-workshop',
            })
          }
          if (data?.role) {
            entities.push({
              id: `persona-role-${session.id}`,
              name: data.role,
              type: 'CustomerSegment',
              domain: 'market',
              source: 'persona-workshop',
            })
          }
        }
      }

      // Extract competitors
      if (competitors.status === 'fulfilled' && Array.isArray(competitors.value)) {
        for (const comp of competitors.value) {
          if (comp.name) {
            entities.push({
              id: `competitor-${comp.id}`,
              name: comp.name,
              type: 'Competitor',
              domain: 'market',
              source: 'competitor-map',
            })
          }
        }
      }

      // Extract BMC items (API returns array of canvases)
      if (bmcCanvas.status === 'fulfilled') {
        const canvases = Array.isArray(bmcCanvas.value)
          ? bmcCanvas.value
          : bmcCanvas.value?.data ? [bmcCanvas.value] : []
        for (const canvas of canvases) {
          const bmcData = canvas.data
          if (!bmcData) continue
          for (const [blockKey, config] of Object.entries(BMC_BLOCK_CONFIG)) {
            const block = bmcData[blockKey as keyof typeof bmcData]
            if (block?.items && Array.isArray(block.items)) {
              for (const item of block.items) {
                if (typeof item === 'string' && item.trim().length >= 3) {
                  entities.push({
                    id: `bmc-${blockKey}-${item.slice(0, 20)}`,
                    name: item.trim(),
                    type: config.type,
                    domain: config.domain,
                    source: 'bmc',
                  })
                }
              }
            }
          }
        }
      }

      return entities
    },
    enabled: isReady && !!accountId && !!projectId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  })
}

// ============================================================================
// TEXT MATCHING
// ============================================================================

/**
 * Find known entities mentioned in document text.
 * Uses case-insensitive whole-word matching.
 * Only matches names >= 3 chars to avoid false positives.
 */
export function findKnownEntityMatches(
  plainText: string,
  knownEntities: KnownEntity[],
): KnownEntityMatch[] {
  if (!plainText || !knownEntities.length) return []

  const matches: KnownEntityMatch[] = []
  const seen = new Set<string>() // Avoid duplicate matches for same name

  for (const entity of knownEntities) {
    const name = entity.name.trim()
    if (name.length < 3) continue

    const key = name.toLowerCase()
    if (seen.has(key)) continue

    // Escape regex special chars and build a word-boundary pattern
    const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const regex = new RegExp(`\\b${escaped}\\b`, 'gi')

    let match: RegExpExecArray | null
    while ((match = regex.exec(plainText)) !== null) {
      matches.push({
        entity,
        evidence: match[0],
        index: match.index,
      })
      seen.add(key)
      break // One match per entity is enough for highlighting
    }
  }

  return matches
}
